import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { MatSelectChange } from '@angular/material/select';
import { FullChild } from '../../../../interfaces/FullChild';
import {PermissionType} from "../../../../components/employee/PermissionType.enum";
import {FinancialYearService} from "../../../../services/financial-year.service";

@Component({
  selector: 'app-age-group-report',
  templateUrl: './age-group-report.component.html',
  styleUrls: ['./age-group-report.component.scss']
})
export class AgeGroupReportComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  mData: any[] = [];
  tableData: any[] = [];
  uniqueCentres: string[] = [];
  uniqueAgeGroup: string[] = [];
  uniqueAgeData: any[] = [];
  followUpAgeData: any[] = [];
  startDate: Date | null = null;
  endDate: Date | null = null;
  fileName = 'Age_Group_Report.xlsx';
  totalColumns: number[] = [];
  grandTotal: number = 0;
  followUpTotalColumns: number[] = [];
  followUpGrandTotal: number = 0;
  selectedCentres: string[] = [];
  types: string[] = ['Standard', 'Detailed'];
  type: string = 'Standard';
  isLoading = false;  // New loading flag
  constructor(private readonly mFirestore: AngularFirestore,
              private readonly financialYearService: FinancialYearService) {}

  ngOnInit(): void {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
  }

  search(): void {
    this.isLoading = true;  // Start loading
    if (this.startDate != null && this.endDate != null) {
      this.mFirestore.collection<FullChild>('ALChild')
        .valueChanges({ idField: 'id' })
        .pipe(takeUntil(this._destroyed$))
        .subscribe(res => {
          this.resetData();

          let childbyadmission: FullChild[] = [];
          res.forEach(it => {
            it.admissionData.forEach((c, i) => {
              if (i % 2 == 0 && c.dateOfAdmission?.toDate()!! >= this.startDate!! && c.dateOfAdmission?.toDate()!! <= this.endDate!!) {
                childbyadmission.push(it);
              }
            });
          });

          this.processChildData(childbyadmission);
          this.reformatTableData();
          this.calculateTotals();
          this.isLoading = false;  // Start loading
        });
    }
  }

  private resetData(): void {
    this.mData = [];
    this.tableData = [];
    this.uniqueCentres = [];
    this.uniqueAgeGroup = [];
    this.uniqueAgeData = [];
    this.followUpAgeData = [];
    this.totalColumns = [];
    this.grandTotal = 0;
    this.followUpTotalColumns = [];
    this.followUpGrandTotal = 0;
    this.isLoading = false;  // Start loading
  }

  private processChildData(childbyadmission: FullChild[]): void {
    childbyadmission.forEach(child => {
      let type = child?.admissionData.length > 2 ? 'Follow Up' : 'Unique';
      let ageGroupInfo = this.type === 'Detailed' ? this.getAgeGroupDetailed(child.age) : this.getAgeGroup(child.age);
      let obj = {
        hospital: child.hospital,
        centre: child.centreName,
        gender: child.gender,
        ageGroup: ageGroupInfo.ageGroup,
        type: type,
        order: ageGroupInfo.order,
        childCode: child.childCode
      };
      this.mData.push(obj);
    });

    this.mData.sort((a, b) => a.order - b.order);
  }

  private reformatTableData(): void {
    const uniqueHospitals = [...new Set(this.mData.map(child => child.hospital))];
    this.uniqueCentres = [...new Set(this.mData.map(child => child.centre))].sort();
    this.uniqueAgeGroup = [...new Set(this.mData.map(child => child.ageGroup))];

    uniqueHospitals.forEach(hospital => {
      let hospitalData: any = { hospital, centres: [] };
      this.uniqueCentres.forEach(centre => {
        let count = this.mData.filter(child => child.hospital === hospital && child.centre === centre).length;
        hospitalData.centres.push({ centre, count });
      });
      this.tableData.push(hospitalData);
    });

    this.uniqueAgeData = this.aggregateData(this.mData, 'Unique');
    this.followUpAgeData = this.aggregateData(this.mData, 'Follow Up');
  }

  private aggregateData(data: any[], type: string): any[] {
    return this.uniqueAgeGroup.map(group => {
      let groupData: any = { ageGroup: group, centres: [] };
      this.uniqueCentres.forEach(centre => {
        let count = data.filter(child => child.ageGroup === group && child.centre === centre && child.type === type).length;
        groupData.centres.push({ centre, count });
      });
      return groupData;
    });
  }

  private calculateTotals(): void {
    this.totalColumns = this.uniqueCentres.map((centre, index) =>
      this.uniqueAgeData.reduce((sum, data) => sum + data.centres[index].count, 0)
    );
    this.grandTotal = this.totalColumns.reduce((sum, count) => sum + count, 0);

    this.uniqueAgeData.forEach(data => {
      data.totalCount = data.centres.reduce((sum: any, centre: { count: any; }) => sum + centre.count, 0);
    });

    this.followUpTotalColumns = this.uniqueCentres.map((centre, index) =>
      this.followUpAgeData.reduce((sum, data) => sum + data.centres[index].count, 0)
    );
    this.followUpGrandTotal = this.followUpTotalColumns.reduce((sum, count) => sum + count, 0);

    this.followUpAgeData.forEach(data => {
      data.totalCount = data.centres.reduce((sum: any, centre: { count: any; }) => sum + centre.count, 0);
    });
  }

  private getAgeGroup(age: number): any {
    let ageGroup = '';
    let order = 8;
    if (age != null) {
      if (age >= 0 && age <= 3) {
        order = 2;
        ageGroup = 'Group 0-3';
      } else if (age >= 4 && age <= 7) {
        order = 3;
        ageGroup = 'Group 4-7';
      } else if (age >= 8 && age <= 10) {
        order = 4;
        ageGroup = 'Group 8-10';
      } else if (age >= 11 && age <= 14) {
        order = 5;
        ageGroup = 'Group 11-14';
      }
      else ageGroup = 'Group 15-18'
    }
    return { ageGroup, order };
  }

  private getAgeGroupDetailed(age: number): any {
    let ageGroup = '';
    let order = 8;
    if (age != null) {
      if (age <= 2 && age > 0) {
        order = 1;
        ageGroup = 'Group 0-2';
      } else if (age >= 3 && age <= 4) {
        order = 2;
        ageGroup = 'Group 3-4';
      } else if (age >= 5 && age <= 6) {
        order = 3;
        ageGroup = 'Group 5-6';
      } else if (age >= 7 && age <= 8) {
        order = 4;
        ageGroup = 'Group 7-8';
      } else if (age >= 9 && age <= 10) {
        order = 5;
        ageGroup = 'Group 9-10';
      } else if (age >= 11 && age <= 12) {
        order = 6;
        ageGroup = 'Group 11-12';
      } else if (age >= 12 && age <= 14) {
        order = 7;
        ageGroup = 'Group 12-14';
      }
      else ageGroup = 'Group 15-18'
    }
    return { ageGroup, order };
  }

  download(): void {
    const excelData = this.mData.map(child => {
      return {
        Hospital: child.hospital,
        Centre: child.centre,
        AgeGroup: child.ageGroup,
        Type: child.type,
        Gender: child.gender
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', this.fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  exportexcel() {

    /* assessment-table id is passed over here */
    let element = document.getElementById('excel-table');

    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    // const ws: XLSX.WorkSheet =XLSX.utils.table_to_sheet(element);
    // const ws: XLSX.WorkSheet =XLSX.utils.json_to_sheet(this.receipts)
    ws['!cols'] = [];
    ws['!cols'][19] = {hidden: true};

    /* O1 is your Column in Excel*/

    /* generate workbook and add the worksheet */
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    /* save to file */
    XLSX.writeFile(wb, this.fileName);



  }
  exportexcel2() {

    /* assessment-table id is passed over here */
    let element = document.getElementById('excel-table2');

    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    // const ws: XLSX.WorkSheet =XLSX.utils.table_to_sheet(element);
    // const ws: XLSX.WorkSheet =XLSX.utils.json_to_sheet(this.receipts)
    ws['!cols'] = [];
    ws['!cols'][19] = {hidden: true};

    /* O1 is your Column in Excel*/

    /* generate workbook and add the worksheet */
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    /* save to file */
    XLSX.writeFile(wb, this.fileName);



  }

  filterCentres($event: MatSelectChange): void {
    this.selectedCentres = $event.value;
  }

  Clear() {
// Clear input fields
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
    this.selectedCentres = []; // Clear selected centres if needed

    // Clear loaded data
    this.mData = [];
    this.tableData = []; // Clear table data
    this.uniqueCentres = [];
    this.uniqueAgeGroup = [];
    this.uniqueAgeData = [];
    this.followUpAgeData = [];
    this.type = '';
    this.uniqueAgeData = [];
    this.followUpAgeData = [];
    this.totalColumns = [];
    this.grandTotal = 0;
    this.followUpTotalColumns = [];
    this.followUpGrandTotal = 0;

  }
  onEndDateChanged() {
    this.endDate?.setHours(23, 59, 0, 0);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  protected readonly PermissionType = PermissionType;
}
